import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import {
  datePickerClearableProps,
  datePickerFormItemProps,
  datePickerStandardProps,
  quillEditorStandardProps,
  quillFormItemProps,
  selectStandardProps,
} from "@/utils/componentUtils";
import { toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Card, Checkbox, Col, DatePicker, Form, Row, Select } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import { rowGutter } from "../../../../../../common/constants";
import { Permission } from "../../../../../../common/security/enums";
import { useHasPermissions } from "../../../../../auth/hooks";
import {
  CommodityCurrency,
  CommodityPaymentFrequency,
  CommodityPaymentType,
  CommoditySavingsPlan,
  CommodityShape,
} from "../../../../enums";
import { CreateUpdateContractGainerRecord, CreateUpdateGenericContract, GenericContract } from "../../../../types";
import { calculateContractStatus, calculateGainerRecordStartDates, isContractVerified } from "../../../../utils";
import ContractStatusTag from "../../../tags/ContractStatusTag";

const colSpan = 4;

interface Props {
  form: FormInstance<CreateUpdateGenericContract>;
  initialContract?: GenericContract;
}

export const GenericContractFormDataSection = ({ form, initialContract }: Props) => {
  const { t } = useTranslation();

  const hasPrivilegedChangesPermission = useHasPermissions(Permission.PRIVILEGED_CHANGES_ON_VERIFIED_GENERIC);

  const disablePrivilegedInputs = isContractVerified(initialContract) && !hasPrivilegedChangesPermission;

  const status = Form.useWatch("status", form);
  const signDate = Form.useWatch("signDate", form);
  const cancellationDate = Form.useWatch("cancellationDate", form);
  const transferredToOtherBroker = Form.useWatch("transferredToOtherBroker", form);
  const commodities = Form.useWatch("commodities", form);

  const transferredToOtherBrokerDateRules = [
    ...(signDate ? [validations.notSameOrBefore(signDate, t("contract.attrs.signDate"))] : []),
    ...(cancellationDate ? [validations.notAfter(cancellationDate, t("contract.attrs.cancellationDate"))] : []),
    ...(transferredToOtherBroker ? [validations.notNull] : []),
  ];

  const handleSignDateChange = () => {
    handleStatusDefiningDateChange();
    const signDate = form.getFieldValue(["signDate"]) as string;
    const records = form.getFieldValue(["gainerRecords"]) as CreateUpdateContractGainerRecord[];
    form.setFieldsValue({ gainerRecords: calculateGainerRecordStartDates(records, signDate) });
  };

  const handleStatusDefiningDateChange = () => {
    const { signDate, cancellationDate, transferredToOtherBrokerDate } = form.getFieldsValue([
      ["signDate"],
      ["cancellationDate"],
      ["transferredToOtherBrokerDate"],
    ]) as CreateUpdateGenericContract;
    form.setFieldsValue({
      status: calculateContractStatus({
        startDate: signDate,
        endDate: undefined,
        cancellationDate: cancellationDate,
        transferredToOtherBrokerDate: transferredToOtherBrokerDate,
      }),
    });
  };

  const handleTransferredToOtherBrokerChange = (event: CheckboxChangeEvent) => {
    if (!event.target.checked) {
      form.setFieldsValue({ transferredToOtherBrokerDate: undefined });
      handleStatusDefiningDateChange();
    }
  };

  return (
    <Card
      type="inner"
      className="card-box card-box--inner-extra"
      title={t("contract.sections.contractData")}
      extra={status ? <ContractStatusTag status={status} /> : undefined}
    >
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="mediationReportSignDate"
            label={t("contract.attrs.mediationReportSignDate")}
            rules={[signDate ? validations.notAfter(signDate, t("contract.attrs.signDate")) : validations.none]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerClearableProps} maxDate={toDate(signDate)} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="signDate"
            label={t("contract.attrs.signDate")}
            rules={[validations.notNull]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} onChange={handleSignDateChange} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="cancellationDate"
            label={t("contract.attrs.cancellationDate")}
            rules={[signDate ? validations.notBefore(signDate, t("contract.attrs.signDate")) : validations.none]}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerClearableProps}
              minDate={toDate(signDate)}
              onChange={handleStatusDefiningDateChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="tradeAmount"
            label={t("contract.attrs.tradeAmount")}
            rules={[validations.notNull, validations.minNumber(0)]}
          >
            <InputNumber type="decimal" addon="euro" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="regularPayment"
            label={t("contract.attrs.regularPayment")}
            rules={[validations.minNumber(0)]}
          >
            <InputNumber type="decimal" addon="euro" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="feeTotalAmount"
            label={t("contract.attrs.feeTotalAmount")}
            rules={[validations.minNumber(0)]}
          >
            <InputNumber type="decimal" addon="euro" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="transferredFromOtherBroker"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={{ maxHeight: 22 }}>
              <LabelWithTooltip
                label={t("contract.attrs.transferredFromOtherBroker")}
                tooltip={t("contract.helpers.transferredFromOtherBrokerDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="transferredToOtherBroker"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={{ maxHeight: 22 }} onChange={handleTransferredToOtherBrokerChange}>
              <LabelWithTooltip
                label={t("contract.attrs.transferredToOtherBroker")}
                tooltip={t("contract.helpers.transferredToOtherBrokerDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="transferredToOtherBrokerDate"
            label={
              <LabelWithTooltip
                label={t("contract.attrs.transferredToOtherBrokerDate")}
                tooltip={t("contract.helpers.transferredToOtherBrokerDateDesc")}
              />
            }
            rules={transferredToOtherBrokerDateRules.length ? transferredToOtherBrokerDateRules : [validations.none]}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerClearableProps}
              disabled={!transferredToOtherBroker}
              minDate={toDate(signDate)?.add(1, "day")}
              maxDate={toDate(cancellationDate)}
              onChange={handleStatusDefiningDateChange}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Form.Item
            name="commodities"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={{ maxHeight: 22 }} onChange={handleTransferredToOtherBrokerChange}>
              {t("contract.attrs.commodities")}
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>

      {commodities && (
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="commodityPaymentType"
              label={t("contract.enums.commodityPaymentType._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={Object.values(CommodityPaymentType).map((type) => ({
                  value: type,
                  label: t(`contract.enums.commodityPaymentType.${type}`),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="commodityPaymentFrequency"
              label={t("contract.enums.commodityPaymentFrequency._label")}
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.values(CommodityPaymentFrequency).map((frequency) => ({
                  value: frequency,
                  label: t(`contract.enums.commodityPaymentFrequency.${frequency}`),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="commodityPiecesCount"
              label={t("contract.attrs.commodityPiecesCount")}
              rules={[validations.minNumber(1), validations.notNull]}
            >
              <InputNumber addon="piece" />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="commodityWeight"
              label={t("contract.attrs.commodityWeight")}
              rules={[validations.minNumber(1), validations.notNull]}
            >
              <InputNumber type="decimal" addon="g" />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="commodityCurrency"
              label={t("contract.enums.commodityCurrency._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={Object.values(CommodityCurrency).map((type) => ({
                  value: type,
                  label: t(`contract.enums.commodityCurrency.${type}`),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="commodityShape"
              label={t("contract.enums.commodityShape._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={Object.values(CommodityShape).map((type) => ({
                  value: type,
                  label: t(`contract.enums.commodityShape.${type}`),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="commoditySavingsPlan"
              label={t("contract.enums.commoditySavingsPlan._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={Object.values(CommoditySavingsPlan).map((type) => ({
                  value: type,
                  label: t(`contract.enums.commoditySavingsPlan.${type}`),
                }))}
                disabled={disablePrivilegedInputs}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={rowGutter} className="margin-top-small">
        <Col span={24}>
          <Form.Item
            name="note"
            label={t("contract.attrs.note")}
            rules={[validations.size(1, 8192)]}
            {...quillFormItemProps}
          >
            <ReactQuill {...quillEditorStandardProps} />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};
